import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGridFilterDisplay } from 'ag-grid-react';
import type { IAfterGuiAttachedParams } from 'ag-grid-community';
import { Option } from 'effect';

import { useAgConditionModel } from '@/components/AgGrid/components/AgDateFilter/hooks/useAgConditionModel';
import { useSubmitAgModel } from '@/components/AgGrid/components/AgDateFilter/hooks/useSubmitAgModel';

import {
  dateFilterModelOperator,
  isSameDateFilterModel,
} from '@/components/AgGrid/components/AgDateFilter/dateFilterModel';

import { parseDateFilterOperator } from '@/components/AgGrid/components/AgDateFilter/operators';
import type { DateFilterOperator } from '@/components/AgGrid/components/AgDateFilter/operators';
import { UseAgDateFilterCoreProps } from '@/components/AgGrid/components/AgDateFilter/types';

// The popup of the custom date filter. Filter handlers are on for every grid, so the grid owns the model: this hook
// only edits it. On client-side grids the row test is `doesDateFilterPass` on the column, not this component.
const useAgDateFilterCore = ({ model, defaultOption, onModelChange }: UseAgDateFilterCoreProps) => {
  // The operator starts from the applied model, so reopening an AND filter does not turn it into OR on Apply.
  const modelOperator = dateFilterModelOperator(model);
  const [operator, setOperator] = useState<'or' | 'and'>(modelOperator);
  useEffect(() => setOperator(modelOperator), [modelOperator]);
  const [closeFilter, setCloseFilter] = useState<(() => void) | undefined>();

  const afterGuiAttached = useCallback((params?: IAfterGuiAttachedParams) => {
    setCloseFilter(() => params?.hidePopup);
  }, []);

  const { primaryConditionData, secondaryConditionData } = useMemo(() => {
    // The model comes from outside (a preset, the browser): an operator the filter does not offer opens as the default.
    const operatorOf = (type: unknown): DateFilterOperator =>
      Option.getOrElse(parseDateFilterOperator(type), () => defaultOption);

    if (model && 'conditions' in model) {
      const [p, s] = model.conditions;
      return {
        primaryConditionData: {
          defaultFromDate: p.dateFrom,
          defaultToDate: p.dateTo,
          defaultCondition: operatorOf(p.type),
        },
        secondaryConditionData: {
          defaultFromDate: s.dateFrom,
          defaultToDate: s.dateTo,
          defaultCondition: operatorOf(s.type),
        },
      };
    }

    return {
      primaryConditionData: {
        defaultFromDate: model?.dateFrom,
        defaultToDate: model?.dateTo,
        defaultCondition: operatorOf(model?.type),
      },
      secondaryConditionData: {
        defaultFromDate: null,
        defaultToDate: null,
        defaultCondition: defaultOption,
      },
    };
  }, [defaultOption, model]);

  const primaryCondition = useAgConditionModel(primaryConditionData);
  const secondaryCondition = useAgConditionModel(secondaryConditionData);
  const isRangeError = primaryCondition.isRangeError || secondaryCondition.isRangeError;

  const resetFilterData = useCallback(() => {
    primaryCondition.resetToDefault();
    secondaryCondition.resetToDefault();
    setOperator(modelOperator);
  }, [primaryCondition, secondaryCondition, modelOperator]);

  const modelSubmitData = useSubmitAgModel({ conditions: [primaryCondition, secondaryCondition], operator });

  useGridFilterDisplay({
    afterGuiAttached,
    afterGuiDetached: resetFilterData,
  });

  const handleAccept = useCallback(() => {
    if (primaryCondition.isError || isRangeError) return;
    // `onModelChange` always fires `filterChanged`, which re-requests the rows of a server-side grid:
    // an Apply that changes nothing must not reach it.
    if (!isSameDateFilterModel(modelSubmitData, model)) {
      onModelChange(modelSubmitData);
    }

    closeFilter?.();
  }, [closeFilter, isRangeError, model, modelSubmitData, onModelChange, primaryCondition]);

  const handleReset = useCallback(() => {
    resetFilterData();
    onModelChange(null);
    closeFilter?.();
  }, [closeFilter, onModelChange, resetFilterData]);

  return {
    operator,
    setOperator,
    closeFilter,
    primaryCondition,
    secondaryCondition,
    handleAccept,
    handleReset,
  };
};

export default useAgDateFilterCore;
